import { Button, Card, LicensePlate } from "@rumbledirect/ui";
import { getFormattedDossierType } from "../../../../lib/formatter/dossiers";
import { Col, Modal, Row, Space, Tooltip, message } from "antd";
import { type DescriptionsProps } from "antd";
import { Descriptions } from "@/components/v2";
import { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import { format, parseISO } from "date-fns";
import { getConfigValue } from "@rumble-it/common-lib/src/config";
import { useAffiliate } from "../../../../lib/hooks/useAffiliate";
import { useCurrentUser } from "../../../../lib/hooks/useCurrentUser";
import {
  archiveDossierById,
  markDossierAsUnreadById,
  unarchiveDossierById,
} from "../../../../lib/api/resources/dossiers";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const DossierSummary = ({
  dossier,
  isLoading,
}: {
  dossier: Dossier | undefined;
  isLoading: boolean;
}) => {
  const { currentAffiliate } = useAffiliate();
  const currentUser = useCurrentUser();
  const permissions = currentUser?.permissions || [];

  const hasPortalLinkPermission = permissions.includes(
    "admin_dossier_link_portal",
  );
  const hasMyLinkPermission = permissions.includes("admin_dossier_link_my");
  const hasArchivePermission = permissions.includes("admin_dossier_archive");
  const hasUnarchivePermission = permissions.includes(
    "admin_dossier_unarchive",
  );
  const hasMarkUnreadPermission = permissions.includes(
    "admin_dossier_unread_view",
  );

  const archivedAt =
    dossier && "archivedAt" in dossier && typeof dossier.archivedAt === "string"
      ? dossier.archivedAt
      : null;

  const isRead = dossier?.adminFirstReadAt != null;

  const dossierId = isLoading ? undefined : dossier?.id;

  const queryClient = useQueryClient();

  const toggleArchiveMutation = useMutation({
    mutationFn: () => {
      if (!dossierId) {
        return Promise.resolve(null);
      }
      return archivedAt
        ? unarchiveDossierById(dossierId)
        : archiveDossierById(dossierId);
    },
    onSuccess: () => {
      if (!dossierId) {
        return;
      }

      message.success(
        archivedAt
          ? "Dossier succesvol gedearchiveerd"
          : "Dossier succesvol gearchiveerd",
      );
      queryClient.invalidateQueries({ queryKey: ["dossier", dossierId] });
      queryClient.invalidateQueries({ queryKey: ["dossiers"] });
    },
    onError: () => {
      message.error("Er is iets misgegaan.");
    },
  });

  const markUnreadMutation = useMutation({
    mutationFn: () => {
      if (!dossierId) {
        return Promise.resolve(null);
      }
      return markDossierAsUnreadById(dossierId);
    },
    onSuccess: () => {
      if (!dossierId) {
        return;
      }

      message.success("Dossier gemarkeerd als ongelezen");
      queryClient.invalidateQueries({ queryKey: ["dossier", dossierId] });
      queryClient.invalidateQueries({ queryKey: ["dossiers"] });
    },
    onError: () => {
      message.error("Er is iets misgegaan.");
    },
  });

  const handleArchiveToggleConfirm = () => {
    const modalRef: { current: ReturnType<typeof Modal.confirm> | null } = {
      current: null,
    };

    modalRef.current = Modal.confirm({
      title: archivedAt ? "Dossier dearchiveren?" : "Dossier archiveren?",
      content: archivedAt
        ? "Weet je zeker dat je dit dossier wilt dearchiveren?"
        : "Weet je zeker dat je dit dossier wilt archiveren?",
      okText: "Bevestigen",
      cancelText: "Annuleren",
      okButtonProps: {
        danger: !archivedAt,
      },
      onOk: async () => {
        modalRef.current?.update({
          cancelButtonProps: {
            disabled: true,
          },
        });
        await toggleArchiveMutation.mutateAsync();
      },
    });
  };

  const PORTAL_URL = getConfigValue("portalUrl");
  const MY_URL = getConfigValue("myUrl");

  const isSyncedDossier = dossier?.ownerAffiliate != null;
  const syncedDossierTooltip =
    "Niet beschikbaar voor dossiers waarvan u geen eigenaar bent";

  const CardActions = (
    <Space>
      {hasPortalLinkPermission &&
        (isSyncedDossier ? (
          <Tooltip title={syncedDossierTooltip}>
            <Button variant="light" disabled>
              Portal
            </Button>
          </Tooltip>
        ) : (
          <a
            target="_blank"
            href={`${PORTAL_URL}?dossierId=${dossier?.id}&affiliateId=${currentAffiliate}`}
            rel="noreferrer"
          >
            <Button variant="light">Portal</Button>
          </a>
        ))}
      {hasMyLinkPermission &&
        (isSyncedDossier ? (
          <Tooltip title={syncedDossierTooltip}>
            <Button variant="light" disabled>
              My
            </Button>
          </Tooltip>
        ) : (
          <a
            target="_blank"
            href={`${MY_URL}?dossierId=${dossier?.id}&affiliateId=${currentAffiliate}`}
            rel="noreferrer"
          >
            <Button variant="light">My</Button>
          </a>
        ))}
      {archivedAt !== null && hasUnarchivePermission ? (
        <Button
          type="primary"
          loading={toggleArchiveMutation.isPending}
          onClick={handleArchiveToggleConfirm}
        >
          Dossier dearchiveren
        </Button>
      ) : null}
      {archivedAt === null && hasArchivePermission ? (
        <Button
          danger
          type="primary"
          loading={toggleArchiveMutation.isPending}
          onClick={handleArchiveToggleConfirm}
        >
          Dossier archiveren
        </Button>
      ) : null}
      {isRead && hasMarkUnreadPermission ? (
        <Button
          variant="light"
          loading={markUnreadMutation.isPending}
          onClick={() => markUnreadMutation.mutate()}
        >
          Markeer ongelezen
        </Button>
      ) : null}
    </Space>
  );

  return (
    <Card
      className="w-full"
      title={"Algemene informatie"}
      loading={isLoading}
      extra={CardActions}
    >
      <Row gutter={[32, 0]} align="top">
        <Col span={24}>
          <Descriptions
            items={
              [
                {
                  label: "Kenteken",
                  children: (
                    <LicensePlate country="NL">
                      {dossier?.vehicle?.licensePlate?.identifier}
                    </LicensePlate>
                  ),
                },
                {
                  label: "Dossier type",
                  children: dossier
                    ? getFormattedDossierType(dossier?.dossierType)
                    : null,
                },
                {
                  label: "Voertuig",
                  children: (
                    <>
                      {dossier?.vehicle?.brand} {dossier?.vehicle?.model}{" "}
                      {dossier?.vehicle?.buildYear &&
                        `(${dossier?.vehicle?.buildYear})`}
                    </>
                  ),
                },
                {
                  label: "Aangemaakt op",
                  children: dossier?.createdAt
                    ? format(parseISO(dossier?.createdAt), "dd-MM-yyyy HH:mm")
                    : null,
                },
                {
                  label: "VIN",
                  children: dossier?.vehicle?.vinNumber ?? null,
                },
                {
                  label: "Kilometerstand",
                  children: dossier?.kilometers
                    ? `${dossier.kilometers.toLocaleString("nl-NL")} km`
                    : null,
                },
                {
                  label: "Verwachte gereed-datum",
                  children: dossier?.expectedFinishDateTime
                    ? format(
                        parseISO(dossier.expectedFinishDateTime),
                        "dd-MM-yyyy",
                      )
                    : null,
                },
                {
                  label: "Eigenaar dossier",
                  children: dossier?.ownerAffiliate?.name ?? "eigen",
                },
              ] satisfies DescriptionsProps["items"]
            }
          />
        </Col>
      </Row>
    </Card>
  );
};
